import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { pedir } from '../api/cliente';
import { Boton, Insignia, Tarjeta } from './ui/basicos';
import { Cargando, Problema } from './ui/estados';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { conDosDecimales } from '../venta/PiezasDeVenta';
import { importeParaMirar } from '../lib/numero';
import { bajarPlanilla } from '../lib/planilla';

interface Comparado {
  readonly hoy: string;
  readonly ayer: string;
  readonly promedioDelMes: string;
}

interface TableroDelDia {
  readonly fecha: string;
  readonly ventas: Comparado & { readonly cantidadHoy: number };
  readonly margen: Comparado;
  readonly plata: {
    readonly enCaja: string;
    readonly enBanco: string;
    readonly enCartera: string;
    readonly enTransito: string;
  };
  readonly cobrar: { readonly pendiente: string; readonly vencido: string };
  readonly faltantes: readonly {
    readonly varianteId: string;
    readonly codigo: string;
    readonly nombre: string;
    readonly vendidoUltimos30: string;
  }[];
  readonly porSucursal: readonly {
    readonly sucursalId: string;
    readonly nombre: string;
    readonly ventas: Comparado;
    readonly margen: string;
    readonly cantidadHoy: number;
    readonly enCaja: string;
  }[];
  readonly sucursales: readonly string[] | null;
}

/** El importe como entero, en diezmilésimos. Es plata: no pasa por coma flotante (regla 2). */
function enBruto(texto: string): bigint {
  const negativo = texto.trim().startsWith('-');
  const [entera = '0', fraccion = ''] = texto.replace('-', '').split('.');
  const bruto = BigInt(entera || '0') * 10_000n + BigInt(fraccion.padEnd(4, '0').slice(0, 4));
  return negativo ? -bruto : bruto;
}

const valorAbsoluto = (valor: bigint): bigint => (valor < 0n ? -valor : valor);

/**
 * Cuánto cambió respecto de la referencia, en porcentaje entero.
 *
 * Nulo cuando no hay con qué comparar: **el primer día de un comercio no es un 0 % de mejora**, y
 * mostrarlo como tal sería afirmar algo que no se sabe.
 *
 * La cuenta va en enteros. No es ceremonia: el porcentaje se calcula sobre importes, y la regla 2
 * del proyecto —nunca coma flotante para plata— no tiene una excepción para «esto es sólo para
 * mostrar».
 */
function variacion(hoy: string, contra: string): number | null {
  const base = enBruto(contra);
  if (base === 0n) return null;
  const diferencia = enBruto(hoy) - base;
  const negativo = diferencia < 0n !== base < 0n;
  const magnitud = valorAbsoluto(base);
  // Redondeo a la mitad para arriba, en enteros: (|d| · 100 · 2 + |b|) / (|b| · 2).
  const entero = (valorAbsoluto(diferencia) * 200n + magnitud) / (magnitud * 2n);
  return Number(negativo ? -entero : entero);
}

/**
 * Un número con su comparación al lado.
 *
 * **La comparación no es un adorno.** «Vendí 400.000» no es una noticia hasta saber que ayer fueron
 * 250.000; sin ella el tablero es una lista de totales, que es lo que ya se puede ver entrando a
 * cada pantalla. Cuando no hay con qué comparar —el primer día del comercio— no se muestra un 0 %,
 * que sería afirmar algo falso: no se muestra nada.
 */
/**
 * Los tonos de los números del tablero.
 *
 * **El color dice qué clase de número es, no adorna**: verde lo que entró, azul dónde está la
 * plata, rojo lo que se pasó de fecha. Un tablero todo negro obliga a leer las etiquetas para
 * saber qué se está mirando; uno con un color por cosa se recorre de un vistazo.
 *
 * `neutro` existe para los que no son plata —la cantidad de operaciones— porque pintarlos de verde
 * los haría parecer una suma de dinero.
 */
const TONOS_DE_CIFRA = {
  neutro: '',
  entra: 'text-bien',
  plata: 'text-acento',
} as const;

function Numero({
  titulo,
  valor,
  ayer,
  promedio,
  tono = 'neutro',
}: {
  readonly titulo: string;
  readonly valor: string;
  readonly ayer?: string;
  readonly promedio?: string;
  readonly tono?: keyof typeof TONOS_DE_CIFRA;
}): React.ReactElement {
  const { t, i18n } = useTranslation();
  const contraAyer = ayer === undefined ? null : variacion(valor, ayer);

  return (
    <div className="min-w-32">
      <p className="text-apoyo text-texto-tenue">{titulo}</p>
      <p className={`cifra text-cifra font-medium ${TONOS_DE_CIFRA[tono]}`}>
        {importeParaMirar(valor, i18n.language)}
      </p>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-texto-tenue">
        {contraAyer === null ? null : (
          <Insignia tono={contraAyer >= 0 ? 'bien' : 'atencion'}>
            {/* La flecha además del color. **No es adorno: es el mismo dato dicho dos veces**, y la
                segunda vez sirve para quien no distingue el verde del ámbar, que es una de cada
                doce personas. */}
            {contraAyer >= 0 ? (
              <TrendingUp className="size-3.5" aria-hidden />
            ) : (
              <TrendingDown className="size-3.5" aria-hidden />
            )}
            {t('tablero.contraAyer', {
              valor: `${contraAyer >= 0 ? '+' : ''}${String(contraAyer)}`,
            })}
          </Insignia>
        )}
        {promedio === undefined ? null : (
          <span>
            {t('tablero.promedio')}:{' '}
            <span className="cifra">{importeParaMirar(promedio, i18n.language)}</span>
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * El tablero del día.
 *
 * Va en la pantalla de inicio y en ese orden: **cuánto vendí, cuánta plata tengo, cuánto me deben,
 * qué me falta**. Es el orden en que un dueño lo piensa, no el orden en que el sistema lo guarda.
 *
 * Quien no tiene el permiso no ve un cartel de «sin acceso»: no ve nada. Un tablero vacío con una
 * explicación es peor que la pantalla que había antes.
 */
export function Tablero(): React.ReactElement | null {
  const { t, i18n } = useTranslation();
  const puedeVer = usePermiso('INFORMES.TABLERO.VER');

  const consulta = useQuery({
    queryKey: ['tablero'],
    enabled: puedeVer,
    queryFn: () => pedir<TableroDelDia>('/informes/tablero'),
  });

  if (!puedeVer) return null;
  if (consulta.isPending) return <Cargando />;
  if (consulta.isError) return <Problema error={consulta.error} />;

  const panel = consulta.data;
  const hayVencido = Number(panel.cobrar.vencido) > 0;

  /**
   * La planilla del tablero.
   *
   * Va en **filas de concepto y valor** y no en una tabla ancha, porque lo que se baja son medidas
   * distintas —plata, cantidades, porcentajes— y ponerlas en columnas obligaría a una fila sola con
   * veinte campos, que en una planilla no se puede filtrar ni sumar.
   *
   * Sale de lo que la pantalla ya tiene: **lo que se baja es exactamente lo que se está mirando**,
   * y no una segunda consulta que podría contestar otra cosa.
   */
  const bajar = (): void => {
    const filas: (string | number)[][] = [
      [t('tablero.vendido'), t('tablero.hoy'), panel.ventas.hoy],
      [t('tablero.vendido'), t('tablero.ayer'), panel.ventas.ayer],
      [t('tablero.vendido'), t('tablero.promedio'), panel.ventas.promedioDelMes],
      [t('tablero.margen'), t('tablero.hoy'), panel.margen.hoy],
      [t('tablero.margen'), t('tablero.ayer'), panel.margen.ayer],
      [t('tablero.margen'), t('tablero.promedio'), panel.margen.promedioDelMes],
      [t('tablero.operaciones'), t('tablero.hoy'), panel.ventas.cantidadHoy],
      [t('tablero.plata'), t('tablero.enCaja'), panel.plata.enCaja],
      [t('tablero.plata'), t('tablero.enBanco'), panel.plata.enBanco],
      [t('tablero.plata'), t('tablero.enCartera'), panel.plata.enCartera],
      [t('tablero.plata'), t('tablero.enTransito'), panel.plata.enTransito],
      [t('tablero.cobrar'), t('tablero.pendiente'), panel.cobrar.pendiente],
      [t('tablero.cobrar'), t('tablero.vencido'), panel.cobrar.vencido],
      ...panel.porSucursal.map((fila) => [t('tablero.porSucursal'), fila.nombre, fila.ventas.hoy]),
      ...panel.faltantes.map((falta) => [
        t('tablero.faltantes'),
        `${falta.codigo} ${falta.nombre}`,
        falta.vendidoUltimos30,
      ]),
    ];
    bajarPlanilla(
      `tablero-${panel.fecha}`,
      [t('tablero.concepto'), t('tablero.detalle'), t('tablero.valor')],
      filas,
    );
  };

  return (
    <div className="flex flex-col gap-4 print:gap-2">
      <Tarjeta>
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-seccion font-semibold tracking-tight">{t('tablero.elDia')}</h2>
          <div className="flex items-center gap-2">
            <span className="text-xs text-texto-tenue">{panel.fecha}</span>
            {/* Los botones no se imprimen: el papel es el tablero, no la pantalla. */}
            <div className="flex gap-2 print:hidden">
              <Boton onClick={bajar}>{t('tablero.planilla')}</Boton>
              <Boton
                onClick={() => {
                  globalThis.print();
                }}
              >
                {t('acciones.imprimir')}
              </Boton>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-8">
          <Numero
            titulo={t('tablero.vendido')}
            valor={panel.ventas.hoy}
            ayer={panel.ventas.ayer}
            promedio={panel.ventas.promedioDelMes}
            tono="entra"
          />
          <Numero
            titulo={t('tablero.margen')}
            valor={panel.margen.hoy}
            ayer={panel.margen.ayer}
            promedio={panel.margen.promedioDelMes}
            tono="entra"
          />
          <div className="min-w-40">
            <p className="text-apoyo text-texto-tenue">{t('tablero.operaciones')}</p>
            <p className="cifra text-cifra font-medium">{panel.ventas.cantidadHoy}</p>
          </div>
        </div>
      </Tarjeta>

      <div className="grid gap-4 md:grid-cols-2">
        <Tarjeta>
          <h2 className="mb-3 text-seccion font-semibold tracking-tight">{t('tablero.plata')}</h2>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Numero titulo={t('tablero.enCaja')} valor={panel.plata.enCaja} tono="plata" />
            <Numero titulo={t('tablero.enBanco')} valor={panel.plata.enBanco} tono="plata" />
            <Numero titulo={t('tablero.enCartera')} valor={panel.plata.enCartera} tono="plata" />
            <Numero titulo={t('tablero.enTransito')} valor={panel.plata.enTransito} tono="plata" />
          </div>
          {/* La caja es la de los turnos abiertos, y el banco no se reparte por sucursal: dos
              cosas que un número solo no puede explicar y que sin la aclaración se leen mal. */}
          <p className="mt-3 text-xs text-texto-tenue">{t('tablero.deDondeSale')}</p>
        </Tarjeta>

        <Tarjeta>
          <h2 className="mb-3 text-seccion font-semibold tracking-tight">{t('tablero.cobrar')}</h2>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Numero titulo={t('tablero.pendiente')} valor={panel.cobrar.pendiente} />
            <div className="min-w-40">
              <p className="text-apoyo text-texto-tenue">{t('tablero.vencido')}</p>
              <p className={`cifra text-cifra font-medium ${hayVencido ? 'text-mal' : ''}`}>
                {importeParaMirar(panel.cobrar.vencido, i18n.language)}
              </p>
            </div>
          </div>
        </Tarjeta>
      </div>

      {/*
        Comparar sucursales. Viene vacío con una sola —comparar un local contra sí mismo es ruido—
        y **con lo que la persona puede ver y nada más**: un encargado restringido a un local no
        recibe siquiera el nombre de los otros.
      */}
      {panel.porSucursal.length === 0 ? null : (
        <Tarjeta>
          <h2 className="mb-2 text-seccion font-semibold tracking-tight">
            {t('tablero.porSucursal')}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-borde text-xs text-texto-tenue">
                  <th className="py-1 text-left font-medium">{t('tablero.sucursal')}</th>
                  <th className="py-1 text-right font-medium">{t('tablero.vendido')}</th>
                  <th className="py-1 text-right font-medium">{t('tablero.contraAyerCorto')}</th>
                  <th className="py-1 text-right font-medium">{t('tablero.margen')}</th>
                  <th className="py-1 text-right font-medium">{t('tablero.operaciones')}</th>
                  <th className="py-1 text-right font-medium">{t('tablero.enCaja')}</th>
                </tr>
              </thead>
              <tbody>
                {panel.porSucursal.map((fila) => {
                  const contra = variacion(fila.ventas.hoy, fila.ventas.ayer);
                  return (
                    <tr key={fila.sucursalId} className="border-b border-borde/50">
                      <td className="py-2">{fila.nombre}</td>
                      <td className="numero py-2 text-right">{conDosDecimales(fila.ventas.hoy)}</td>
                      <td className="numero py-2 text-right text-xs text-texto-tenue">
                        {contra === null
                          ? '—'
                          : t('tablero.porciento', {
                              valor: `${contra >= 0 ? '+' : ''}${String(contra)}`,
                            })}
                      </td>
                      <td className="numero py-2 text-right">{conDosDecimales(fila.margen)}</td>
                      <td className="numero py-2 text-right">{fila.cantidadHoy}</td>
                      <td className="numero py-2 text-right">{conDosDecimales(fila.enCaja)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* La sucursal que no vendió nada aparece en cero y no se esconde: que un local no haya
              vendido **es la noticia**, y ocultarlo deja la comparación inútil justo cuando sirve. */}
          <p className="mt-2 text-xs text-texto-tenue">{t('tablero.sucursalEnCero')}</p>
        </Tarjeta>
      )}

      {panel.faltantes.length === 0 ? null : (
        <Tarjeta>
          <h2 className="mb-1 text-seccion font-semibold tracking-tight">
            {t('tablero.faltantes')}
          </h2>
          {/* Lo que se quedó en cero **y se vendió**: un artículo en cero que nadie compra no es un
              faltante, es una decisión vieja. */}
          <p className="mb-2 text-xs text-texto-tenue">{t('tablero.faltantesExplicacion')}</p>
          <ul className="flex flex-col divide-y divide-borde">
            {panel.faltantes.map((falta) => (
              <li key={falta.varianteId} className="flex items-center gap-3 py-2 text-sm">
                <span className="min-w-0 flex-1">
                  {falta.nombre}
                  <span className="block text-xs text-texto-tenue">{falta.codigo}</span>
                </span>
                <span className="numero text-xs text-texto-tenue">
                  {t('tablero.vendidas', { cuantas: conDosDecimales(falta.vendidoUltimos30) })}
                </span>
              </li>
            ))}
          </ul>
        </Tarjeta>
      )}
    </div>
  );
}
